import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@vsolv/core/users/web';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Policy, Provisioning, Warranty } from '@wsphere/warranties/domain';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  from,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FeatureFlagsService } from '../../../policy/services/feature-flags.service';
import { CHECKOUT_CONFIG } from '../../config/checkout.config';
import {
  BfsFundingStatus,
  BfsLoanQuoteService,
  LemurApplicationWebService,
  ProvisioningSessionService,
} from '../../services';

const BFS_FUNDING_STATUSES: readonly BfsFundingStatus[] = ['activated', 'pending', 'unavailable', 'failed'];

/** Posted by the server's BFS callback page once it has checked funding with Budco. */
interface BfsCallbackMessage {
  type: 'bfs-callback';
  dealId: string;
  status: BfsFundingStatus;
}

interface BudcoProductMapping {
  productId: string;
  policyPrefixes: string[];
}

interface BudcoProductMappingConfig {
  version: 1;
  mappings: BudcoProductMapping[];
}

type LegacyBudcoProductMapping = {
  productId?: unknown;
  policyPrefixes?: unknown;
};

@Component({
  selector: 'ws-warranty-preview-card',
  templateUrl: './warranty-preview-card.component.html',
  styleUrls: ['./warranty-preview-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WarrantyPreviewCardComponent implements OnInit, OnDestroy {
  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private toastSvc: ToastService,
    private portalSvc: PortalService,
    private userSvc: UserService,
    private featureFlagsSvc: FeatureFlagsService,
    private bfsLoanQuoteSvc: BfsLoanQuoteService,
    private lemurApplicationSvc: LemurApplicationWebService,
    private sessionSvc: ProvisioningSessionService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef
  ) {}

  @HostBinding('class') _class = 'block w-full';

  @Input() termsAndConditions = false;
  @PropertyListener('termsAndConditions') termsAndConditions$ = new BehaviorSubject<boolean>(this.termsAndConditions);

  @Output() back = new EventEmitter();

  @ViewChild('input') paymentMethodInput?: { _state: 'select' | 'credit-card' | 'bank' };
  @ViewChild('phaseOneInput') phaseOneMethodInput?: { _state: 'select' | 'credit-card' | 'bank' };
  @ViewChild('phaseTwoInput') phaseTwoMethodInput?: { _state: 'select' | 'credit-card' | 'bank' };
  @ViewChild('bfsIframe') private bfsIframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild('markupWarningDialog') markupWarningDialog?: DialogComponent;
  private pendingPaymentAction: (() => void) | null = null;

  @PropertyListener('selectedPaymentSchedule') selectedPaymentSchedule$ =
    new BehaviorSubject<Policy.PaymentSchedule | null>(null);
  selectedPaymentSchedule: Policy.PaymentSchedule | null = null;

  // Pay later option - when true, user skips payment during checkout
  payLater = false;
  /** Staff confirmed a priced sale with no payment schedule is sold on Pay Later (VS-1451). */
  payLaterAcknowledged = false;

  /** Lemur is an alternative to ordinary checkout, never a replacement for pay now. */
  lemurPaymentMode: 'pay-now' | 'finance' = 'pay-now';

  // Staff-only split: phase 1 covers the first N installments, phase 2 the remainder,
  // each with its own method and payer.
  multiPhaseEnabled = false;
  phaseOneInstallments = new FormControl<number | null>(null, [Validators.required, Validators.min(1)]);
  phaseOneBillTo = false;
  phaseOneDueDate = new FormControl('', { nonNullable: true });
  phaseOneMethod = new FormControl(null as string | null, [Validators.required]);
  phaseTwoMethod = new FormControl(null as string | null, [Validators.required]);
  // Defaults to the common deal: seller covers phase 1, customer takes the rest.
  protected phaseOneSellerPays$ = new BehaviorSubject<boolean>(true);
  protected phaseTwoSellerPays$ = new BehaviorSubject<boolean>(false);

  // UI state for the combined Budco (Pay Now + Finance) flow
  isFinanceWithBfs = false;
  bfsQuoteMode: 'full-pay' | 'finance' = 'finance';
  isRequestingQuote = false;
  bfsActivationUrl: SafeResourceUrl | null = null;
  /**
   * True once BFS returns a success activation URL, meaning a pending warranty was already
   * created server-side. Prevents switching to another payment option for the same session,
   * which would result in a 409 ConflictException.
   */
  hasBfsPendingWarranty = false;
  private pendingBfsActivationUrl: SafeResourceUrl | null = null;
  private bfsPendingContractNumber: string | null = null;
  private bfsActivationPollingTimeout: ReturnType<typeof setTimeout> | null = null;
  private bfsActivationPollingAttempt = 0;
  /** Each poll asks the server to check funding with Budco, so poll gently: 30 × 10s = 5 minutes. */
  private readonly BFS_ACTIVATION_POLL_INTERVAL_MS = 10_000;
  private readonly BFS_ACTIVATION_POLL_MAX_ATTEMPTS = 30;
  private bfsStaffView = true;
  /** Latest Budco funding check for the pending BFS warranty; `waiting` until one has answered. */
  bfsFundingState: Exclude<BfsFundingStatus, 'activated'> | 'waiting' = 'waiting';
  isCheckingBfsFunding = false;
  bfsDownPaymentAmount = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);
  bfsTermLength = new FormControl<number | null>(null, [
    Validators.required,
    Validators.min(CHECKOUT_CONFIG.BUDCO_MIN_TERM_MONTHS),
    Validators.max(CHECKOUT_CONFIG.BUDCO_MAX_TERM_MONTHS),
  ]);
  /** Top-level payment timing choice: BFS combined flow or confirm without payment. */
  bfsSelectedOption: 'budco' | null = null;
  /** Sub-choice inside Pay with BFS: pay in full (term 0) or finance. */
  bfsPaymentMode: 'pay-now' | 'finance' | null = null;
  private bfsRequestVersion = 0;
  /** In-flight Registration PDF prefetch started when Budco is selected. */
  private bfsContractProofPrefetch: Promise<unknown> | null = null;

  isLoading = true;

  termsDoc: Policy.Document | null = null;

  paymentMethod = new FormControl(null as string | null, [Validators.required]);

  contactEmail = this.portalSvc.config.contactEmail;

  protected session$ = this.sessionSvc.getCurrentSession();

  protected preview$ = this.sessionSvc.preview$.asObservable();

  protected warranty$ = new BehaviorSubject<Warranty.Model | null>(null);

  protected payingForCustomer$ = new BehaviorSubject<boolean>(false);

  protected isProcessingProvisioning$ = new BehaviorSubject<boolean>(false);

  protected currentUser$ = this.userSvc.getSelf();

  // Resolved here rather than with chained async pipes: a pipe subscribing after the toggle
  // flips can miss getSelf's emission and leave the picker on the wrong party's cards.
  protected phaseOneUserId$ = this.phasePayerUserId(this.phaseOneSellerPays$);
  protected phaseTwoUserId$ = this.phasePayerUserId(this.phaseTwoSellerPays$);

  private phasePayerUserId(sellerPays$: BehaviorSubject<boolean>) {
    // getSelf() is a promise and must not gate the first emission — a null userId makes the
    // picker list payment methods tenant-wide. Fall back to the customer until it resolves.
    const currentUser$ = from(this.currentUser$).pipe(startWith(null));

    return combineLatest([sellerPays$, currentUser$, this.session$]).pipe(
      map(([sellerPays, user, session]) => {
        const customerUserId = session?.customer?.userId ?? null;
        return sellerPays ? (user?.id ?? customerUserId) : customerUserId;
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  protected bfsEnabled$ = this.featureFlagsSvc.isEnabledState('BFS_FINANCING_ENABLED');
  protected paymentOptionsLoadState$ = this.featureFlagsSvc.loadState$;
  protected lemurEnabled$ = this.featureFlagsSvc.isEnabled('LEMUR_FINANCING_ENABLED');

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  protected bfsConfigured$ = from(this.bfsLoanQuoteSvc.getConfigStatus()).pipe(
    map(response => Boolean(response?.configured && response?.dealershipConfigured && response?.budcoProductId)),
    catchError(() => of(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected lemurConfigured$ = from(this.lemurApplicationSvc.getConfigStatus()).pipe(
    map(status => Boolean(status?.usable)),
    catchError(() => of(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected canFinanceWithLemur$ = combineLatest([
    this.bfsEnabled$,
    this.lemurEnabled$,
    this.lemurConfigured$,
    this.session$,
    this.preview$,
  ]).pipe(
    map(([bfsEnabled, enabled, usable, session, preview]) => {
      const expiry = session?.expiryDate ? new Date(session.expiryDate).getTime() : null;
      const active = expiry === null || expiry > Date.now();
      const positivePayment = (preview?.purchase?.payments?.[0]?.amount ?? 0) > 0;
      return Boolean(
        bfsEnabled === false &&
        enabled &&
        usable &&
        active &&
        positivePayment &&
        session?.id &&
        session.customerId &&
        session.policyId &&
        session.planId
      );
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Ownership is independent of readiness: a broken BFS configuration must never expose another provider.
  protected canFinanceWithBfs$ = this.bfsEnabled$;

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  // Centralized config
  readonly checkoutConfig = CHECKOUT_CONFIG;

  ngOnInit(): void {
    this.paymentOptionsLoadState$.pipe(take(1)).subscribe(state => {
      if (state === 'error') this.featureFlagsSvc.refresh();
    });
  }

  retryPaymentOptions(): void {
    this.featureFlagsSvc.refresh();
  }

  selectedTerm$ = this.preview$.pipe(
    map(preview => preview?.session.policy?.terms.find(term => term.id === preview.session.policyTermId) ?? null)
  );

  paymentSchedules$ = combineLatest([this.selectedTerm$, this.selectedPaymentSchedule$]).pipe(
    switchMap(async ([term, selectedPaymentSchedule]) => {
      const oneTime = term?.paymentSchedules.find(schedule => schedule.installments === 1) ?? null;
      const installments =
        term?.paymentSchedules
          .filter(schedule => schedule.installments !== 1)
          .sort((a, b) => b.installments - a.installments) ?? [];

      if (!selectedPaymentSchedule && (oneTime || installments.length)) {
        await this.selectPaymentSchedule(oneTime ?? installments[0]);
      }

      return { oneTime, installments };
    })
  );

  baseCoverages$ = this.preview$.pipe(
    tap(preview =>
      preview?.warranty.policy?.termsAndConditions
        ? (this.termsDoc = preview.warranty.policy.termsAndConditions)
        : undefined
    ),
    map(preview => preview?.warranty.coverages.filter(coverage => coverage.requirement === 'BASE'))
  );

  selectedAddons$ = this.preview$.pipe(
    map(preview => preview?.addons),
    tap(() => (this.isLoading = false))
  );

  evaluateTermsAndConditions(termsAndConditions: boolean) {
    this.termsAndConditions = termsAndConditions;
  }

  selectLemurPaymentMode(mode: 'pay-now' | 'finance'): void {
    this.lemurPaymentMode = mode;
    this.payLater = false;
    if (mode === 'finance') this.setMultiPhaseEnabled(false);
    this.cdr.markForCheck();
  }

  private resetBfsQuoteState() {
    this.bfsRequestVersion += 1;
    this.isFinanceWithBfs = false;
    this.isRequestingQuote = false;
    this.bfsActivationUrl = null;
    this.bfsQuoteMode = 'finance';
    this.bfsDownPaymentAmount.reset();
    this.bfsTermLength.reset();
    this.bfsSelectedOption = null;
    this.bfsPaymentMode = null;
    this.hasBfsPendingWarranty = false;
    this.pendingBfsActivationUrl = null;
    this.bfsPendingContractNumber = null;
    this.bfsContractProofPrefetch = null;
    this.bfsFundingState = 'waiting';
    this.stopBfsActivationPolling();
  }

  setPayLater(value: boolean) {
    this.payLater = value;
    if (value) {
      this.resetBfsQuoteState();
      this.setMultiPhaseEnabled(false);
    }
  }

  setMultiPhaseEnabled(enabled: boolean) {
    this.multiPhaseEnabled = enabled;
    if (!enabled) {
      this.phaseOneInstallments.reset();
      this.phaseOneBillTo = false;
      this.phaseOneDueDate.reset();
      this.phaseOneMethod.reset();
      this.phaseTwoMethod.reset();
      this.phaseOneSellerPays$.next(true);
      this.phaseTwoSellerPays$.next(false);
    }
  }

  setPhaseOneBillTo(billTo: boolean) {
    this.phaseOneBillTo = billTo;
    if (billTo) this.phaseOneMethod.reset();
  }

  // Flipping the payer swaps whose methods the picker lists — drop any selection made for the other party.
  setPhaseOneSellerPays(sellerPays: boolean) {
    this.phaseOneSellerPays$.next(sellerPays);
    this.phaseOneMethod.reset();
  }

  setPhaseTwoSellerPays(sellerPays: boolean) {
    this.phaseTwoSellerPays$.next(sellerPays);
    this.phaseTwoMethod.reset();
  }

  /** Installments in the selected schedule — the total the two phases must add up to. */
  get totalInstallments(): number {
    return this.selectedPaymentSchedule?.installments ?? 1;
  }

  /** Installments phase 2 automatically covers — everything phase 1 doesn't. */
  phaseTwoInstallmentCount(totalInstallments: number): number {
    return Math.max(totalInstallments - (this.phaseOneInstallments.value ?? 0), 0);
  }

  isMultiPhaseValid(totalInstallments: number): boolean {
    const count = this.phaseOneInstallments.value;
    // Integer check mirrors the server's @IsInt, so a decimal is caught here instead of as a raw 400.
    if (!count || !Number.isInteger(count) || count < 1 || count >= totalInstallments) return false;
    if (!this.phaseOneBillTo && !this.phaseOneMethod.value) return false;
    return !!this.phaseTwoMethod.value;
  }

  /** Surface why the sale is blocked, rather than only disabling confirm. */
  get phaseOneCountError(): boolean {
    const count = this.phaseOneInstallments.value;
    if (count === null || count === undefined) return this.phaseOneInstallments.touched;
    return !Number.isInteger(count) || count < 1 || count >= this.totalInstallments;
  }

  // Keyed off the count, not `touched`: the picker's trigger is disabled when a party has no
  // saved cards, so it can never be touched — exactly when the message is needed.
  private get phaseCountReady(): boolean {
    const count = this.phaseOneInstallments.value;
    return !!count && Number.isInteger(count) && count >= 1 && count < this.totalInstallments;
  }

  get phaseOneMethodError(): boolean {
    return this.phaseCountReady && !this.phaseOneBillTo && !this.phaseOneMethod.value;
  }

  get phaseTwoMethodError(): boolean {
    return this.phaseCountReady && !this.phaseTwoMethod.value;
  }

  /** Gates the confirm button: valid multi-phase config, or a selected single method. */
  isPaymentSelectionValid(): boolean {
    if (this.sessionSvc.preview$.value?.paymentCollectionUnavailable) return false;
    if (this.multiPhaseEnabled) return this.isMultiPhaseValid(this.totalInstallments);
    return this.paymentMethod.valid;
  }

  /** True while no payment-method picker is mid-way through adding a new card/bank account. */
  get paymentInputsIdle(): boolean {
    if (this.multiPhaseEnabled) {
      return (
        (!this.phaseOneMethodInput || this.phaseOneMethodInput._state === 'select') &&
        (!this.phaseTwoMethodInput || this.phaseTwoMethodInput._state === 'select')
      );
    }
    return this.paymentMethodInput?._state === 'select';
  }

  private buildMultiPhasePayment(): Provisioning.MultiPhasePayment {
    const installments = this.phaseOneInstallments.value ?? 0;

    return {
      phaseOne: this.phaseOneBillTo
        ? {
            installments,
            billTo: true,
            owner: this.phaseOneSellerPays$.value ? 'seller' : 'customer',
            ...(this.phaseOneSellerPays$.value ? { dueDate: this.phaseOneDueDate.value || null } : {}),
          }
        : { installments, paymentMethodId: this.phaseOneMethod.value },
      phaseTwo: { paymentMethodId: this.phaseTwoMethod.value ?? '' },
    };
  }

  /**
   * Admin-dashboard-only guard (VS-1116): warns a seller who is about to finalize a sale without
   * ever having entered a markup. Fired right before the actions that actually commit the sale
   * (BFS handoff, skip-payment confirm, standard confirm) rather than every intermediate payment
   * UI toggle, so the seller sees it once, with time to go back and add a markup, not repeatedly.
   */
  private guardMarkupThenProceed(staffView: boolean, action: () => void): void {
    const preview = this.sessionSvc.preview$.value;
    const markupAllowed = Policy.isMarkupAllowed(preview?.session?.policy);
    const hasMarkup = (preview?.markup ?? 0) > 0;

    if (!staffView || !markupAllowed || hasMarkup) {
      action();
      return;
    }

    this.pendingPaymentAction = action;
    this.markupWarningDialog?.open();
  }

  confirmProceedWithoutMarkup() {
    const action = this.pendingPaymentAction;
    this.pendingPaymentAction = null;
    this.markupWarningDialog?.close();
    action?.();
  }

  cancelProceedWithoutMarkup() {
    this.pendingPaymentAction = null;
    this.markupWarningDialog?.close();
  }

  onSelectBudcoPayment(sessionId: string | null | undefined, staffView: boolean) {
    this.guardMarkupThenProceed(staffView, () => this.selectBudcoPayment(sessionId, staffView));
  }

  onSkipPaymentAndConfirm(sessionId: string, staffView: boolean) {
    this.guardMarkupThenProceed(staffView, () => this.skipPaymentAndConfirm(sessionId, staffView));
  }

  onProvision(sessionId: string, hash: string, staffView: boolean) {
    this.guardMarkupThenProceed(staffView, () => this.provision(sessionId, hash, staffView));
  }

  selectBudcoPayment(sessionId: string | null | undefined, staffView: boolean) {
    this.payLater = false;
    this.resetBfsQuoteState();
    this.bfsSelectedOption = 'budco';
    this.startBfsContractProofPrefetch(sessionId, staffView);
  }

  selectBfsPayNow(sessionId: string | null | undefined, staffView: boolean) {
    this.payLater = false;
    this.bfsSelectedOption = 'budco';
    this.bfsPaymentMode = 'pay-now';
    this.startBfsContractProofPrefetch(sessionId, staffView);
    void this.requestBfsQuote(sessionId, { termLength: 0 }, staffView);
  }

  selectBfsFinance(sessionId: string | null | undefined, staffView: boolean) {
    this.payLater = false;
    this.bfsSelectedOption = 'budco';
    this.bfsPaymentMode = 'finance';
    this.bfsDownPaymentAmount.reset();
    this.bfsTermLength.reset();
    this.startBfsContractProofPrefetch(sessionId, staffView);
    void this.syncBfsDownPaymentValidators();
  }

  private async syncBfsDownPaymentValidators(): Promise<void> {
    const preview = await firstValueFrom(this.preview$);
    const minDownPayment = preview ? this.getMinDownPaymentAmount(this.getRetailPrice(preview)) : 0.01;
    this.bfsDownPaymentAmount.setValidators([Validators.required, Validators.min(minDownPayment)]);
    this.bfsDownPaymentAmount.updateValueAndValidity();
  }

  getMinDownPaymentPercentDisplay(): number {
    return CHECKOUT_CONFIG.BUDCO_MIN_DOWN_PAYMENT_PERCENT * 100;
  }

  async submitBfsFinanceQuote(sessionId: string | null | undefined, staffView: boolean) {
    const termLength = this.bfsTermLength.value;
    const downPaymentAmount = this.bfsDownPaymentAmount.value;

    if (!termLength || downPaymentAmount == null) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to start financing',
        text: 'Please enter a down payment amount and payment term.',
      });
      return;
    }

    const preview = await firstValueFrom(this.preview$);
    const retailPrice = this.getRetailPrice(preview);
    const minDownPayment = this.getMinDownPaymentAmount(retailPrice);

    if (downPaymentAmount < minDownPayment) {
      this.toastSvc.show({
        type: 'error',
        title: 'Down payment too low',
        text: `Down payment must be at least ${minDownPayment.toFixed(2)} (${this.getMinDownPaymentPercentDisplay()}% of the purchase price).`,
      });
      return;
    }

    if (termLength < CHECKOUT_CONFIG.BUDCO_MIN_TERM_MONTHS || termLength > CHECKOUT_CONFIG.BUDCO_MAX_TERM_MONTHS) {
      this.toastSvc.show({
        type: 'error',
        title: 'Invalid payment term',
        text: `Payment term must be between ${CHECKOUT_CONFIG.BUDCO_MIN_TERM_MONTHS} and ${CHECKOUT_CONFIG.BUDCO_MAX_TERM_MONTHS} months.`,
      });
      return;
    }

    await this.requestBfsQuote(
      sessionId,
      {
        termLength,
        downPaymentAmount,
        collectDownPaymentViaBfs: true,
      },
      staffView
    );
  }

  getBillingTotalCents(preview: Provisioning.WarrantyPreview | null | undefined): number {
    if (!preview) return 0;
    let total = preview.subtotal + (preview.markup ?? 0);
    for (const tax of preview.taxes ?? []) {
      total += tax.amount;
    }
    // The first payment also carries the one-time initiation fee and its own tax (VS-1448).
    if (preview.initiationFee) {
      total += preview.initiationFee.amount;
      for (const tax of preview.initiationFee.taxes) total += tax.amount;
    }
    return total;
  }

  /**
   * A priced sale on a term with no payment schedule can't be paid at checkout, so it may only go
   * ahead as Pay Later, acknowledged explicitly (VS-1451). The server rejects it otherwise.
   */
  needsPayLaterAcknowledgement(
    preview: Provisioning.WarrantyPreview | null | undefined,
    term: Policy.PolicyTerm | null | undefined
  ): boolean {
    return !!term && !term.paymentSchedules?.length && (preview?.subtotal ?? 0) + (preview?.markup ?? 0) > 0;
  }

  /** Retail price in dollars — matches the order summary "Billing total". */
  getRetailPrice(preview: Provisioning.WarrantyPreview | null | undefined): number {
    return this.getBillingTotalCents(preview) / 100;
  }

  getMinDownPaymentAmount(retailPrice: number): number {
    return Math.round(retailPrice * CHECKOUT_CONFIG.BUDCO_MIN_DOWN_PAYMENT_PERCENT * 100) / 100;
  }

  getMaxDownPaymentAmount(retailPrice: number): number {
    return Math.round(retailPrice * 100) / 100;
  }

  closeFinanceWithBfs() {
    this.bfsRequestVersion += 1;
    this.isFinanceWithBfs = false;
    this.isRequestingQuote = false;
    this.bfsActivationUrl = null;
    this.bfsQuoteMode = 'finance';
    this.bfsPaymentMode = null;
    // hasBfsPendingWarranty and pendingBfsActivationUrl are intentionally NOT reset here —
    // the pending warranty still exists server-side and the user must resume or start a new session.
  }

  reopenBfsIframe() {
    if (this.pendingBfsActivationUrl) {
      this.bfsActivationUrl = this.pendingBfsActivationUrl;
      this.cdr.detectChanges();
    }
  }

  /** Manual recheck, e.g. after polling gave up or Budco was unavailable. */
  async checkBfsFunding(): Promise<void> {
    const contractNumber = this.bfsPendingContractNumber;
    if (!contractNumber || this.isCheckingBfsFunding) return;

    this.isCheckingBfsFunding = true;
    this.cdr.detectChanges();
    try {
      const status = await this.verifyBfsFunding(contractNumber);
      if (status === 'activated') {
        await this.completeBfsCheckout(contractNumber);
        return;
      }
      this.applyBfsFundingStatus(status);
      if (status !== 'failed') this.startBfsActivationPolling();
    } finally {
      this.isCheckingBfsFunding = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.stopBfsActivationPolling();
  }

  private fetchWarrantyByContractNumber(contractNumber: string): Promise<Warranty.Model | null> {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${contractNumber}`, {
          params: { idField: 'contractNumber' },
        })
        .pipe(catchError(() => of(null)))
    );
  }

  private fetchWarrantyById(warrantyId: string): Promise<Warranty.Model | null> {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${encodeURIComponent(warrantyId)}`, {
          params: { idField: 'id' },
        })
        .pipe(catchError(() => of(null)))
    );
  }

  async completeLemurCheckout(warrantyId: string): Promise<void> {
    const warranty = await this.fetchWarrantyById(warrantyId);
    if (!warranty || warranty.status !== 'ACTIVATED') return;

    this.warranty$.next(warranty);
    this.sessionSvc.sessionComplete$.next(true);
    this.sessionSvc.clearSession();
    this.sessionSvc.distributor$.next(null);
    this.sessionSvc.addons$.next(null);
    this.sessionSvc.preview$.next(null);
    this.toastSvc.show({
      type: 'success',
      title: 'Warranty Provisioned',
      text: `Your contract number is ${warranty.contractNumber}`,
    });
    this.cdr.detectChanges();
  }

  private async requestBfsQuote(
    sessionId: string | null | undefined,
    options: {
      termLength?: number;
      downPaymentAmount?: number;
      collectDownPaymentViaBfs?: boolean;
    },
    staffView = true
  ) {
    if (!sessionId) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to start financing',
        text: 'No checkout session was found. Please refresh and try again.',
      });
      return;
    }

    const requestVersion = ++this.bfsRequestVersion;
    this.isRequestingQuote = true;
    this.toastSvc.show({
      type: 'info',
      title: 'Preparing financing',
      text: 'Please wait while we generate your provisional contract...',
    });

    try {
      // Wait for any in-flight Registration PDF prefetch so the loan-quote call can reuse it.
      await this.bfsContractProofPrefetch;

      const preview = await firstValueFrom(this.preview$);
      const bfsConfigStatus = await this.bfsLoanQuoteSvc.getConfigStatus();
      const contractProofOptions = this.getBfsContractProofOptions(preview);
      const policyPrefix = this.getPolicyPrefixForBudcoMapping(preview);
      const budcoProductId = this.resolveBudcoProductId(policyPrefix, bfsConfigStatus.budcoProductId ?? undefined);
      const response = await this.bfsLoanQuoteSvc.requestLoanQuoteFromSession(
        sessionId,
        {
          ...options,
          ...contractProofOptions,
          returnUrl: this.buildBfsFrontendReturnUrl(),
          ...(budcoProductId ? { budcoProductId } : {}),
        },
        staffView
      );
      if (requestVersion !== this.bfsRequestVersion) return;

      // BFS returns HTTP 200 even for a `failure` result, so check the result, not the status.
      if (response.result !== 'success' || !response.activateLoanQuoteUrl) {
        this.isFinanceWithBfs = false;
        this.bfsActivationUrl = null;
        this.toastSvc.show({
          type: 'error',
          title: 'Unable to start financing',
          text: response.message || 'BFS could not generate a loan quote. Please try again.',
        });
        return;
      }

      // Load the BFS-hosted activation page directly in the iframe.
      // A pending warranty is now created server-side — lock UI to this BFS session.
      this.isFinanceWithBfs = true;
      this.bfsActivationUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.activateLoanQuoteUrl);
      this.pendingBfsActivationUrl = this.bfsActivationUrl;
      this.hasBfsPendingWarranty = true;
      this.bfsPendingContractNumber = response.contractNumber ?? null;
      this.bfsStaffView = staffView;
      this.bfsFundingState = 'waiting';
      this.bfsQuoteMode = options.termLength === 0 ? 'full-pay' : 'finance';
      this.startBfsActivationPolling();
      this.cdr.detectChanges();
    } catch (err) {
      if (requestVersion !== this.bfsRequestVersion) return;
      this.isFinanceWithBfs = false;
      this.bfsActivationUrl = null;
      this.payLater = false;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to start financing',
        text: this.getErrorMessage(err, 'We could not reach the financing provider. Please try again.'),
      });
    } finally {
      if (requestVersion === this.bfsRequestVersion) {
        this.isRequestingQuote = false;
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * AC #7 — Detect when BFS redirects the iframe back to our frontend callback URL.
   * Cross-origin frames throw on location access; a successful read means we are back on
   * the same origin, so we close the iframe and poll for activation.
   */
  onBfsIframeLoad(target: EventTarget | null): void {
    if (!target) return;
    const iframe = target as HTMLIFrameElement;
    try {
      const href = iframe.contentWindow?.location.href;
      if (href && href.startsWith(window.location.origin)) {
        const dealId = new URL(href).searchParams.get('deal_id');
        if (!dealId) return;

        this.bfsPendingContractNumber = dealId;
        this.isFinanceWithBfs = false;
        this.bfsActivationUrl = null;
        this.startBfsActivationPolling();
      }
    } catch {
      // Still on the cross-origin BFS page — nothing to do yet.
    }
  }

  @HostListener('window:message', ['$event'])
  onBfsCallbackMessage(event: MessageEvent): void {
    if (!this.isBfsCallbackMessage(event.data)) return;
    if (event.origin !== window.location.origin) return;
    if (this.bfsIframe?.nativeElement.contentWindow && event.source !== this.bfsIframe.nativeElement.contentWindow) {
      return;
    }

    this.isFinanceWithBfs = false;
    this.bfsActivationUrl = null;
    this.bfsPendingContractNumber = event.data.dealId;

    // The server has already checked funding with Budco; only 'activated' means the warranty is active.
    if (event.data.status === 'activated') {
      void this.completeBfsCheckout(event.data.dealId);
      return;
    }

    this.applyBfsFundingStatus(event.data.status);
    if (event.data.status === 'failed') {
      this.stopBfsActivationPolling();
      this.toastSvc.show({
        type: 'error',
        title: 'Budco Financial Services (BFS) financing was not completed.',
        text: 'The warranty remains pending.',
      });
    } else {
      if (event.data.status === 'unavailable') {
        this.toastSvc.show({
          type: 'warning',
          title: 'Payment not confirmed yet',
          text: "We couldn't confirm payment with Budco right now. The warranty stays pending until we can.",
        });
      }
      this.startBfsActivationPolling();
    }
    this.cdr.detectChanges();
  }

  /** Show the checkout completion screen; user navigates to the warranty manually. */
  private async completeBfsCheckout(contractNumber: string): Promise<void> {
    this.stopBfsActivationPolling();
    this.bfsPendingContractNumber = contractNumber;

    const warranty = await this.fetchWarrantyByContractNumber(contractNumber);
    if (warranty?.status === 'ACTIVATED') {
      this.handleBfsWarrantyActivated(warranty);
      return;
    }

    // Callback may finish before activation is visible — keep polling until ready.
    this.startBfsActivationPolling();
  }

  private isBfsCallbackMessage(data: unknown): data is BfsCallbackMessage {
    if (!data || typeof data !== 'object') return false;
    const message = data as Partial<BfsCallbackMessage>;
    return (
      message.type === 'bfs-callback' &&
      typeof message.dealId === 'string' &&
      BFS_FUNDING_STATUSES.includes(message.status as BfsFundingStatus)
    );
  }

  private applyBfsFundingStatus(status: Exclude<BfsFundingStatus, 'activated'>): void {
    this.bfsFundingState = status;
    this.cdr.detectChanges();
  }

  /** A failed request is reported as `unavailable`: it says nothing about whether the deal was paid. */
  private verifyBfsFunding(contractNumber: string): Promise<BfsFundingStatus> {
    return this.bfsLoanQuoteSvc
      .verifyFunding(contractNumber, this.bfsStaffView)
      .catch((): BfsFundingStatus => 'unavailable');
  }

  private buildBfsFrontendReturnUrl(): string {
    const url = new URL(this.router.url.split('?')[0], window.location.origin);
    return url.toString();
  }

  /**
   * Kick off Registration PDF generation as soon as Budco is selected so it overlaps with
   * Pay Now / Finance UI. Failures are ignored — the loan-quote path regenerates if needed.
   */
  private startBfsContractProofPrefetch(sessionId: string | null | undefined, staffView: boolean) {
    if (!sessionId || this.bfsContractProofPrefetch) return;

    this.bfsContractProofPrefetch = this.bfsLoanQuoteSvc
      .prepareContractProof(sessionId, {}, staffView)
      .catch(() => null);
  }

  /**
   * Auto-wire contract proof into BFS quote requests when the session/preview already exposes it.
   * Prefer storage item id so backend can resolve a fresh signed/encrypted URL at send time.
   */
  private getBfsContractProofOptions(preview: Provisioning.WarrantyPreview | null): {
    contractProofStorageItemId?: string;
    contractProofPdfUrl?: string;
  } {
    const warranty = preview?.warranty;
    if (!warranty) return {};

    const metadata = (warranty.metadata ?? null) as Record<string, unknown> | null;
    const bfsMeta = metadata?.['bfs'] as Record<string, unknown> | undefined;

    const contractProofStorageItemId =
      this.readString(metadata?.['contractProofStorageItemId']) ??
      this.readString(metadata?.['registrationStorageItemId']) ??
      this.readString(metadata?.['declarationStorageItemId']) ??
      this.readString(bfsMeta?.['contractProofStorageItemId']) ??
      this.readString(bfsMeta?.['registrationStorageItemId']) ??
      this.readString(bfsMeta?.['declarationStorageItemId']) ??
      this.readString(warranty.contractId);

    if (contractProofStorageItemId) {
      return { contractProofStorageItemId };
    }

    const contractProofPdfUrl =
      this.readString(metadata?.['contractProofPdfUrl']) ??
      this.readString(metadata?.['registrationPdfUrl']) ??
      this.readString(metadata?.['declarationPdfUrl']) ??
      this.readString(bfsMeta?.['contractProofPdfUrl']) ??
      this.readString(bfsMeta?.['registrationPdfUrl']) ??
      this.readString(bfsMeta?.['declarationPdfUrl']) ??
      this.readString(warranty.contract?.downloadUrl ?? null);

    return contractProofPdfUrl ? { contractProofPdfUrl } : {};
  }

  private readString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  private getPolicyPrefixForBudcoMapping(preview: Provisioning.WarrantyPreview | null): string | undefined {
    const policy = preview?.session?.policy ?? preview?.warranty?.policy;
    if (!policy) return undefined;

    const referencePrefix = this.readString((policy as { referenceId?: string | null }).referenceId)?.toUpperCase();
    if (referencePrefix) return referencePrefix;

    const policyNumber = this.readString((policy as { policyNumber?: string | null }).policyNumber);
    if (!policyNumber) return undefined;

    const numberPrefix = policyNumber.split('-')[0]?.trim().toUpperCase();
    return numberPrefix || undefined;
  }

  private resolveBudcoProductId(policyPrefix: string | undefined, rawConfig: string | undefined): string | undefined {
    const normalizedRaw = this.readString(rawConfig);
    if (!normalizedRaw) return undefined;

    const mappingConfig = this.parseBudcoMappingConfig(normalizedRaw);
    if (!mappingConfig) {
      return normalizedRaw.startsWith('{') ? undefined : normalizedRaw;
    }

    const normalizedPrefix = policyPrefix?.trim().toUpperCase();

    if (normalizedPrefix) {
      const directMatch = mappingConfig.mappings.find(mapping =>
        mapping.policyPrefixes.some(prefix => this.matchesPolicyPrefixPattern(normalizedPrefix, prefix))
      );
      if (directMatch?.productId) return directMatch.productId;
    }

    const wildcard = mappingConfig.mappings.find(mapping => mapping.policyPrefixes.some(prefix => prefix === '*'));
    if (wildcard?.productId) return wildcard.productId;

    return undefined;
  }

  private matchesPolicyPrefixPattern(policyPrefix: string, pattern: string): boolean {
    const normalizedPattern = pattern.trim().toUpperCase();
    if (!normalizedPattern) return false;

    if (normalizedPattern === '*') return true;

    if (!normalizedPattern.includes('*')) {
      return policyPrefix === normalizedPattern;
    }

    const escapedPattern = normalizedPattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    const wildcardRegex = new RegExp(`^${escapedPattern}$`);

    return wildcardRegex.test(policyPrefix);
  }

  private parseBudcoMappingConfig(raw: string): BudcoProductMappingConfig | null {
    if (!raw.startsWith('{')) return null;

    try {
      const parsed = JSON.parse(raw) as Partial<BudcoProductMapping> &
        Partial<BudcoProductMappingConfig> &
        LegacyBudcoProductMapping;

      const mappingsSource = Array.isArray(parsed?.mappings)
        ? parsed.mappings
        : [
            {
              productId: parsed?.productId,
              policyPrefixes: parsed?.policyPrefixes,
            },
          ];

      const mappings = mappingsSource
        .map(mapping => {
          const productId = this.readString(mapping?.productId);
          const policyPrefixes = Array.isArray(mapping?.policyPrefixes)
            ? mapping.policyPrefixes
                .map(prefix => (typeof prefix === 'string' ? prefix.trim().toUpperCase() : ''))
                .filter(Boolean)
            : [];
          return productId && policyPrefixes.length ? { productId, policyPrefixes } : null;
        })
        .filter((mapping): mapping is BudcoProductMapping => Boolean(mapping));

      return mappings.length ? { version: 1, mappings } : null;
    } catch {
      return null;
    }
  }

  private stopBfsActivationPolling(): void {
    if (this.bfsActivationPollingTimeout) {
      clearTimeout(this.bfsActivationPollingTimeout);
      this.bfsActivationPollingTimeout = null;
    }
    this.bfsActivationPollingAttempt = 0;
  }

  private startBfsActivationPolling(): void {
    if (!this.bfsPendingContractNumber) return;

    if (this.bfsActivationPollingTimeout) {
      clearTimeout(this.bfsActivationPollingTimeout);
      this.bfsActivationPollingTimeout = null;
    }

    this.bfsActivationPollingAttempt = 0;
    this.pollBfsActivation();
  }

  private pollBfsActivation(): void {
    const contractNumber = this.bfsPendingContractNumber;
    if (!contractNumber) return;

    if (this.bfsActivationPollingAttempt >= this.BFS_ACTIVATION_POLL_MAX_ATTEMPTS) {
      this.stopBfsActivationPolling();
      return;
    }

    this.bfsActivationPollingTimeout = setTimeout(async () => {
      this.bfsActivationPollingAttempt += 1;

      const status = await this.verifyBfsFunding(contractNumber);
      if (status === 'activated') {
        const warranty = await this.fetchWarrantyByContractNumber(contractNumber);
        if (warranty?.status === 'ACTIVATED') {
          this.handleBfsWarrantyActivated(warranty);
          return;
        }
      } else {
        // Before the buyer finishes the Budco form this is normally 'pending'; keep showing 'waiting'.
        if (status !== 'pending' || this.bfsFundingState !== 'waiting') this.applyBfsFundingStatus(status);
        if (status === 'failed') {
          this.stopBfsActivationPolling();
          return;
        }
      }

      this.pollBfsActivation();
    }, this.BFS_ACTIVATION_POLL_INTERVAL_MS);
  }

  private handleBfsWarrantyActivated(warranty: Warranty.Model): void {
    this.stopBfsActivationPolling();

    this.isFinanceWithBfs = false;
    this.bfsActivationUrl = null;
    this.hasBfsPendingWarranty = false;
    this.pendingBfsActivationUrl = null;
    this.bfsPendingContractNumber = null;
    this.bfsFundingState = 'waiting';

    this.warranty$.next(warranty);
    this.sessionSvc.sessionComplete$.next(true);
    this.sessionSvc.clearSession();
    this.sessionSvc.distributor$.next(null);
    this.sessionSvc.addons$.next(null);
    this.sessionSvc.preview$.next(null);

    this.cdr.detectChanges();
  }

  provision(sessionId: string, hash: string, staffView: boolean) {
    if (this.multiPhaseEnabled) this.confirm(sessionId, hash, staffView, null, this.buildMultiPhasePayment());
    else this.confirm(sessionId, hash, staffView, this.paymentMethod.value);
  }

  /**
   * Skip payment and confirm directly - used when term.paymentOptional is true
   * Updates session with skip marker then confirms without payment method
   */
  async skipPaymentAndConfirm(sessionId: string, staffView: boolean) {
    try {
      this.isProcessingProvisioning$.next(true);

      const preview = await firstValueFrom(this.preview$);
      if (!preview) {
        throw new Error('Session preview not available');
      }

      await this.sessionSvc.updateSession({
        sessionId: preview.session.id,
        data: { paymentSchedule: { id: null } as unknown as Policy.PaymentSchedule },
      });

      await this.sessionSvc.refreshSession();
      const updatedSession = await firstValueFrom(this.session$);
      if (!updatedSession) {
        throw new Error('Unable to retrieve updated session');
      }

      // Confirm without payment method
      this.confirm(sessionId, updatedSession.hash, staffView, undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      this.isProcessingProvisioning$.next(false);

      this.toastSvc.show({
        type: 'error',
        title: 'Failed to process warranty',
        text: this.getErrorMessage(err, 'Could not process the warranty. Please try again.'),
      });
    }
  }

  goBack(session: Provisioning.ProvisioningSession | null, staffView: boolean) {
    const addons = this.sessionSvc.addons$.value;
    const hasAddons = addons?.find(addon => addon.planId === session?.plan?.id)?.addons.length;
    if (staffView) {
      if (!hasAddons) this.navigateTo('/provisioning/plan');
      else this.navigateTo('/provisioning/addon');
    } else {
      if (!hasAddons) this.navigateTo('/checkout/plan');
      else this.navigateTo('/checkout/addon');
    }
  }

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  async confirm(
    sessionId: string,
    hash: string,
    staffView: boolean,
    paymentMethodId?: string | null,
    multiPhasePayment?: Provisioning.MultiPhasePayment
  ) {
    try {
      this.isProcessingProvisioning$.next(true);
      const warranty = await this.sessionSvc.confirm(sessionId, hash, paymentMethodId || undefined, multiPhasePayment);
      this.warranty$.next(warranty);

      this.sessionSvc.sessionComplete$.next(true);
      this.sessionSvc.clearSession();
      this.sessionSvc.distributor$.next(null);
      this.sessionSvc.addons$.next(null);
      this.sessionSvc.preview$.next(null);

      // FIXME: Provisioning session local storage
      // if (!staffView) localStorage.removeItem('provisioning-session');

      this.toastSvc.show({
        type: 'success',
        title: 'Warranty Provisioned',
        text: `Your contract number is ${warranty.contractNumber}`,
      });

      this.isProcessingProvisioning$.next(false);
      this.cdr.detectChanges();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);

      this.isProcessingProvisioning$.next(false);

      if (err.status === 409) {
        this.toastSvc.show({
          type: 'error',
          title: 'Cannot provision warranty',
          text: `This session already has a warranty provisioned`,
        });
      } else if (err.status === 400) {
        this.toastSvc.show({
          type: 'error',
          title: 'Invalid data',
          text: this.getErrorMessage(err, 'Please review warranty details before confirming.'),
        });
      } else if (err.status === 500) {
        this.toastSvc.show({
          type: 'error',
          title: 'Internal Server Error',
          text: `Something went wrong while provisioning the warranty. Please check your payment configuration or try again.`,
        });
      } else {
        this.toastSvc.show({
          type: 'error',
          title: 'Provisioning failed',
          text:
            typeof err.message === 'string'
              ? err.message
              : 'Could not finalize the warranty. Please check your internet connection or try again.',
        });
      }
    }
  }

  private getErrorMessage(err: unknown, fallback: string): string {
    const responseMessage = (err as { error?: { message?: unknown } })?.error?.message;
    if (Array.isArray(responseMessage)) {
      const firstMessage = responseMessage.find(message => typeof message === 'string');
      if (firstMessage) return firstMessage;
    }

    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage;
    }

    const defaultMessage = (err as { message?: unknown })?.message;
    if (typeof defaultMessage === 'string' && defaultMessage.trim()) {
      return defaultMessage;
    }

    return fallback;
  }

  async selectPaymentSchedule(paymentSchedule: Policy.PaymentSchedule | null) {
    const preview = await firstValueFrom(this.preview$);
    if (!preview) return;
    this.selectedPaymentSchedule = paymentSchedule;

    // Multi-phase needs at least two installments to split; a shorter schedule can also leave
    // a phase 1 count that no longer fits, so clear it rather than silently disabling confirm.
    const installments = paymentSchedule?.installments ?? 1;
    if (installments <= 1) this.setMultiPhaseEnabled(false);
    else if ((this.phaseOneInstallments.value ?? 0) >= installments) this.phaseOneInstallments.reset();

    await this.sessionSvc.updateSession({
      sessionId: preview.session.id,
      data: { paymentSchedule: paymentSchedule ?? undefined },
    });
    // const response = await this.sessionSvc.preview(preview.session.id, preview.)
  }
}
